import React, { useState, useEffect } from 'react'

import { ExplicitChangeTextInput } from '../../../common/components/explicit-change-text-input'
import { Select } from '../../../common/components/select'
import { selectComponentService } from '../../../common/services/select-component.service'
import { usePageEditorContext } from '../../contexts/page-editor.context'
import { formatOptionsService, FORMAT_OPTION_NUMBERS, FORMAT_OPTION_PERCENTS } from '../format-options.service'
import { cellFormatEditService } from './cell-format-edit.service'
import { CELL_FORMAT_TYPE } from './table-edit.constants'

import { useStyles } from './cell-format-edit.style'

const cellTypeOptions = [
  { value: undefined, label: 'None' },
  { value: CELL_FORMAT_TYPE.NUMBER, label: 'Number' },
  { value: CELL_FORMAT_TYPE.PERCENT_BAR, label: 'Percent Bar' },
  { value: CELL_FORMAT_TYPE.CONFIDENCE, label: 'Confidence/Forest' },
  { value: CELL_FORMAT_TYPE.TEXT, label: 'Text' }
]

const numberFormatOptions = formatOptionsService.getFormatOptions([
  ...FORMAT_OPTION_NUMBERS,
  ...FORMAT_OPTION_PERCENTS
])

export const CellFormatEdit = function ({ variableOptions }) {
  const classes = useStyles()

  const pageEditorContext = usePageEditorContext() || {}
  const currentEdit = pageEditorContext.currentEdit || {}
  const block = currentEdit.block || {}
  block.props = block.props || {}
  const cellFormats = block.props.cellFormats

  const [cellFormatOptions, setCellFormatOptions] = useState(getCellFormatOptions(cellFormats))
  const [cellFormat, setCellFormat] = useState(getDefaultCellFormatOption(cellFormatOptions))
  const [activeTab, setActiveTab] = useState(0)
  const [cellFormatPart, setCellFormatPart] = useState(getCellFormatPart(cellFormat, activeTab))
  const selectedCellFormat = getSelectedOption(cellTypeOptions, cellFormatPart.format)

  useEffect(() => {
    if (cellFormats) {
      const cellFormatOptions = getCellFormatOptions(cellFormats)
      setCellFormatOptions(cellFormatOptions)

      let newCellFormat
      if (cellFormat) {
        // if there is already one selected and it's valid, use that
        newCellFormat = cellFormatOptions.find(({ value }) => cellFormat.value === value)
      }
      if (!newCellFormat) {
        // if there is not already one selected or it's not in the list of options, select the first option
        newCellFormat = cellFormatOptions.length > 0 ? cellFormatOptions[0] : undefined
      }
      setCellFormat(newCellFormat)
    }
  }, [cellFormats])

  useEffect(() => {
    setCellFormatPart(getCellFormatPart(cellFormat, activeTab))
  }, [cellFormat, activeTab])

  function getCellFormatOptions (cellFormats = {}) {
    return Object.keys(cellFormats).map((value) => ({ value, label: value }))
  }

  function getDefaultCellFormatOption (cellFormatOptions) {
    return cellFormatOptions && cellFormatOptions[0]
  }

  function getCellFormatPart (cellFormat, index) {
    const cellFormats = block.props.cellFormats || {}
    const cellFormatKey = (cellFormat || {}).value
    const cellFormatParts = cellFormats[cellFormatKey] || []
    if (cellFormatParts.length > index) {
      return cellFormatParts[index]
    }
    return {}
  }

  function updateCellFormatPart (updates = {}) {
    const cellFormatKey = (cellFormat || {}).value

    block.props.cellFormats = { ...block.props.cellFormats }
    block.props.cellFormats[cellFormatKey] = cellFormatEditService.updateCellFormatPart(
      block.props.cellFormats[cellFormatKey] || [],
      activeTab,
      updates
    )

    setCellFormatPart(block.props.cellFormats[cellFormatKey][activeTab] || {})
    updateBlock(block)
  }

  function removeCellFormatPart () {
    const cellFormatKey = (cellFormat || {}).value

    block.props.cellFormats = { ...block.props.cellFormats }
    block.props.cellFormats[cellFormatKey] = cellFormatEditService.removeCellFormatPart(
      block.props.cellFormats[cellFormatKey] || [],
      activeTab
    )

    setCellFormatPart(block.props.cellFormats[cellFormatKey][activeTab] || {})
    updateBlock(block)
  }

  function getSelectedOption (options, selectedValue) {
    return options.find(({ value }) => value === selectedValue) || null
  }

  function updateBlock (block) {
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit({ block: { ...block } })
  }

  if (!block.dataKey) {
    return null
  }

  return (
    <div className="flex-column">
      <div data-qa="format">
        <Select
          label="Format"
          options={cellFormatOptions}
          value={cellFormat}
          size="medium"
          onChange={(cellFormat) => setCellFormat(cellFormat)} />
      </div>
      <div className={classes.tabs}>
        {['Primary', 'Secondary'].map((label, index) => (
          <div
            data-qa={`tab-${index}`}
            key={index}
            className={activeTab === index ? classes.activeTab : classes.tab}
            onClick={() => setActiveTab(index)}
          >
            {label}
          </div>
        ))}
      </div>
      <div className={classes.tabContent}>
        <div className={classes.tabContentField} data-qa="cell-type-select">
          <Select
            label="Cell Type"
            options={cellTypeOptions}
            value={selectedCellFormat}
            size="medium"
            onChange={({ value }) => {
              if (value === undefined) {
                removeCellFormatPart()
              } else {
                const updates = { format: value }
                // Default to first variable if not already set
                if (cellFormatPart.variables === undefined && variableOptions.length > 0) {
                  updates.variables = [variableOptions[0].value]
                }
                updateCellFormatPart(updates)
              }
            }} />
        </div>
        {selectedCellFormat !== null && selectedCellFormat.value !== undefined &&
          <VariablePickers
            fieldClassName={classes.tabContentField}
            cellFormatType={cellFormatPart.format}
            options={variableOptions}
            values={cellFormatPart.variables}
            onChange={(values) => {
              updateCellFormatPart({ variables: values })
            }}
          />
        }
        {[CELL_FORMAT_TYPE.NUMBER, CELL_FORMAT_TYPE.CONFIDENCE].includes(cellFormatPart.format) && (
          <div className={classes.tabContentField} data-qa="number-format">
            <Select
              label="Number Format"
              options={numberFormatOptions}
              value={getSelectedOption(numberFormatOptions, cellFormatPart.type)}
              size="medium"
              onChange={({ value }) => {
                updateCellFormatPart({ type: value })
              }} />
          </div>
        )}
        {cellFormatPart.format === CELL_FORMAT_TYPE.CONFIDENCE && <>
          <div className={classes.tabContentField} data-qa="reference-line-value">
            <ExplicitChangeTextInput
              label="Reference Line Value"
              size="medium"
              value={cellFormatPart.referenceline !== undefined ? cellFormatPart.referenceline.toString() : ''}
              onChange={(event) => {
                updateCellFormatPart({ referenceline: Number(event.target.value) })
              }}
            />
          </div>
          <div className={classes.tabContentFieldGrid}>
            {[
              ['min', 'Minimum'],
              ['max', 'Maximum']
            ].map(([prop, label]) => (
              <ExplicitChangeTextInput
                key={prop}
                size="medium"
                label={label}
                value={cellFormatPart[prop] !== undefined ? cellFormatPart[prop].toString() : ''}
                onChange={(event) => {
                  const value = event.target.value === '' ? undefined : Number(event.target.value)
                  updateCellFormatPart({ [prop]: value })
                }}
              />
            ))}
          </div>
        </>}
      </div>
    </div>
  )
}

function VariablePickers ({ fieldClassName, cellFormatType, options, values = [], onChange }) {
  let labels = ['Variable']
  if (cellFormatType === CELL_FORMAT_TYPE.NUMBER) {
    labels = ['Variable', 'Additional Variable']
  } else if (cellFormatType === CELL_FORMAT_TYPE.CONFIDENCE) {
    labels = ['Variable', 'Lower Confidence Variable', 'Upper Confidence Variable']
  }

  return <>
    {labels.map((label, index) => {
      let selectOptions = options
      if (index > 0 && index === (values.length - 1)) {
        selectOptions = [ { value: undefined, label: 'None' }, ...options ]
      }
      return (
        <div key={label} data-qa={`cell-format-${index}`} className={fieldClassName}>
          <Select
            label={label}
            options={selectOptions}
            value={selectComponentService.getSelectedValue(options, values[index])}
            size="medium"
            isDisabled={index > values.length}
            onChange={({ value }) => {
              const updatedValues = [ ...values ]
              updatedValues[index] = value
              onChange(updatedValues.filter((v) => v !== undefined && v !== null))
            }} />
        </div>
      )
    })}
  </>
}
